/**
 * Aggiunge il parametro `returnTo` ai link che aprono il lettore email:
 * il lettore lo usa per riportare l'utente alla pagina di origine alla
 * chiusura del modale (X, overlay, Esc) o dopo l'invio di rispondi/inoltra.
 * Se siamo gia' nella webmail (/emails) l'url resta invariato: chiudendo
 * il lettore l'utente deve restare nella webmail stessa.
 */
export function withReturnTo(url: string): string {
  if (typeof window === 'undefined') return url;
  const { pathname, search } = window.location;
  if (pathname.startsWith('/emails')) return url;
  const target = new URL(url, window.location.origin);
  if (target.searchParams.has('returnTo')) {
    return `${target.pathname}${target.search}${target.hash}`;
  }
  target.searchParams.set('returnTo', `${pathname}${search}`);
  return `${target.pathname}${target.search}${target.hash}`;
}

export function emailMessageHref(messageId: string | number | null | undefined): string | null {
  const parsed = Number(messageId);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) return null;
  return withReturnTo(`/emails?emailId=${parsed}`);
}

/** Closing a deep-linked message keeps the mailbox view but removes the
 * message anchor, so a reload does not reopen the message just dismissed. */
export function emailListHrefAfterClose(pathAndSearch: string): string {
  const target = new URL(pathAndSearch, 'https://crm.local');
  target.searchParams.delete('emailId');
  target.searchParams.delete('messageId');
  return `${target.pathname}${target.search}`;
}

/** Opening a message from the mailbox is a navigable step. Keep queue and
 * folder filters in the URL so Back returns to the exact list and links from
 * the reader can return to this message. */
export function emailMessageHrefInMailbox(pathAndSearch: string, messageId: number): string {
  const target = new URL(pathAndSearch, 'https://crm.local');
  target.searchParams.delete('messageId');
  target.searchParams.set('emailId', String(messageId));
  return `${target.pathname}${target.search}`;
}

/**
 * Gestore unico per qualunque link generato da dati backend (dashboard di
 * Martina, promemoria, segnali, riepiloghi) che potrebbe puntare al lettore
 * email. Prima di questo helper, 4 componenti diversi duplicavano lo stesso
 * `href.startsWith('/emails') ? withReturnTo(href) : href` — un fix
 * applicato in uno si perdeva regolarmente negli altri tre (22 lug 2026).
 * Usare sempre questo (o il componente DashboardLink che lo applica) invece
 * di richiamare withReturnTo a mano su un href che arriva dal backend.
 */
export function emailAwareHref(href: string): string {
  return href.startsWith('/emails') ? withReturnTo(href) : href;
}
